import { createEventHook } from '@vueuse/core'
import type { AxiosError, AxiosPromise, AxiosResponse } from 'axios'
import { ref, shallowRef } from 'vue'
import { useToast } from '@/foundation/toast/use-toast'
import type { UseApiReturn } from '../types'

/**
 * The shape we normalize all “response with error body” into.
 */
type ErrorData = {
  status: number
  code?: string
  message: string
  details?: any
}

/**
 * A composable function for handling API requests with Axios.
 * Provides reactive state and event hooks for managing API responses and errors.
 */
export function useApi<T = any, E = any>(request: AxiosPromise<T>, legacy: boolean = true): UseApiReturn<T, E | ErrorData> {
  const { toast } = useToast()

  // Reactive state
  const response = shallowRef<AxiosResponse<T>>()
  const data = shallowRef<T>()
  const error = shallowRef<E | ErrorData>()
  const isLoading = ref(true)
  const isFinished = ref(false)

  // Event hooks
  const fetchResult = createEventHook<T>()
  const fetchError = createEventHook<AxiosError<E | ErrorData, any>>()

  /**
   * Try parsing JSON to decide between structured errors vs string message.
   */
  const isValidJSON = (str: string) => {
    try {
      JSON.parse(str)

      return true
    } catch {
      return false
    }
  }

  /**
   * Given a server response with an error‐body, normalize it.
   */
  function extractErrorData(res: AxiosResponse<any>): ErrorData {
    const d = res.data as any

    return {
      status: res.status,
      code: d.errorCode ?? d.statusCode ?? d.code,
      message: d.errorDescription ?? d.message ?? d.error,
      details: d.meta ?? d.details,
    }
  }

  /**
   * Render one or many “destructive” toasts from our normalized ErrorData.
   */
  function showToastFor(errData: ErrorData) {
    if (errData.message && isValidJSON(errData.message)) {
      JSON.parse(errData.message).forEach((e: any) => {
        if (e.message) {
          toast({ title: e.message, variant: 'destructive' })
        }
      })
    } else {
      toast({
        title: errData.message || 'An error occurred',
        variant: 'destructive',
      })
    }
  }

  /**
   * The single catch callback for all Axios errors.
   */
  function handleError(e: AxiosError<E>) {
    if (e.response) {
      if (e.response.status === 304) {
        response.value = e.response as any
        data.value = null as any
        fetchResult.trigger(null as any)

        return
      }

      const errData = extractErrorData(e.response)

      error.value = errData
      if (e.response.status !== 401) {
        showToastFor(errData)
      }
      fetchError.trigger(legacy ? (errData as any) : (e as any))
    } else {
      console.error(e.request ?? e.message)
      toast({
        title: e.request ? 'No response received from server' : e.message || 'An error occurred',
        variant: 'destructive',
      })
      error.value = e as any
      fetchError.trigger(e as any)
    }
  }

  // Kick off the request
  request
    .then((r) => {
      response.value = r
      data.value = r.data
      fetchResult.trigger(r.data)
    })
    .catch(handleError)
    .finally(() => {
      isLoading.value = false
      isFinished.value = true
    })

  return {
    response,
    data,
    error,
    finished: isFinished,
    loading: isLoading,
    onSuccess: fetchResult.on,
    onError: fetchError.on,
    asPromised: () => request.then((r) => r.data),
  }
}
